import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowLeft } from "lucide-react";

// Bump this constant AND the backend `CURRENT_TOS_VERSION` in tandem whenever
// the terms below change materially — logged-in users are then re-prompted to
// accept the new version via the <TosGate/> modal.
export const TOS_VERSION = "2026-02-15";

/**
 * Terms of Use / EULA — Apple UGC requirement.
 *
 * Accessible three ways:
 *   1. Direct URL `/terms`
 *   2. Registration screen (users must actively agree)
 *   3. Profile page → "Terms of Use" link
 */
export default function Terms() {
    return (
        <div data-testid="terms-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:py-14">
            <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-3xl p-6 sm:p-8 text-white shadow-xl"
                style={{ background: "linear-gradient(135deg, #f4a261 0%, #ee8f6d 45%, #dd6b4d 100%)" }}
            >
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-white/20 blur-2xl" aria-hidden />
                <div className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" aria-hidden />
                <div className="relative flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/95 text-primary flex items-center justify-center shadow-lg shrink-0">
                        <ShieldCheck className="w-7 h-7" strokeWidth={2.2} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/80">Legal</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight">Terms of Use</h1>
                        <div className="text-white/85 text-xs sm:text-sm mt-1">Version {TOS_VERSION}</div>
                    </div>
                </div>
            </motion.section>

            <div className="mt-6">
                <Link
                    to="/"
                    data-testid="terms-back-link"
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to home
                </Link>
            </div>

            <div className="mt-6 space-y-8 text-[15px] leading-relaxed text-foreground/90">
                <p>
                    Welcome to ErasmusSwap. By creating an account, using the app, or accessing the website you agree to the
                    following Terms of Use. If you do not agree, please do not use ErasmusSwap.
                </p>

                <Section title="1. What ErasmusSwap is">
                    <p>
                        ErasmusSwap is a free, non-commercial platform that helps Erasmus and other exchange students find each other
                        so they can swap rooms or connect as Exchange Buddies. ErasmusSwap is not a party to any rental agreement
                        between users and does not verify listings or identities. You use the platform at your own risk.
                    </p>
                </Section>

                <Section title="2. Who can use ErasmusSwap">
                    <p>
                        You must be at least 16 years old to use ErasmusSwap. You confirm that the information you provide about
                        yourself is accurate and that you are legally allowed to enter into these terms in your country.
                    </p>
                </Section>

                <Section title="3. Your content and your responsibilities">
                    <p>
                        You are fully responsible for the content you upload, including room photos, room descriptions, profile
                        information and any messages you send. By uploading content you confirm that you own it or have the right to
                        share it.
                    </p>
                    <p>The following is <strong>strictly prohibited</strong> on ErasmusSwap:</p>
                    <ul className="list-disc pl-6 space-y-1.5">
                        <li>Objectionable, illegal, abusive, fraudulent, hateful, discriminatory, sexually explicit or otherwise
                            inappropriate content.</li>
                        <li>Harassment, bullying, threats or abusive behaviour toward other users.</li>
                        <li>Misleading, dishonest or fraudulent room listings (e.g. fake photos, false prices, non-existent rooms).</li>
                        <li>Spam, scams, phishing, payment requests outside the platform's agreed rental amount.</li>
                        <li>Any activity that violates applicable law.</li>
                    </ul>
                </Section>

                <Section title="4. Moderation">
                    <p>
                        We may — at our sole discretion and without prior notice — remove any content, suspend an account, or
                        permanently ban a user who violates these terms. We take action on reports within 24 hours where reasonably
                        possible.
                    </p>
                    <p>
                        We do <strong>not tolerate</strong> objectionable content or abusive users. Repeat offenders are removed
                        permanently.
                    </p>
                </Section>

                <Section title="5. Reporting and blocking">
                    <p>
                        Every room listing includes a <em>Report</em> option. If you see content that violates these terms, please
                        report it — reports are reviewed and appropriate action is taken. You can also <em>Block</em> a user; once
                        blocked, that user's listings will not appear in your feed and they can no longer message you.
                    </p>
                    <p>
                        Abuse of the reporting mechanism (e.g. mass-reporting a competitor's listing) is itself a violation of these
                        terms.
                    </p>
                </Section>

                <Section title="6. Housing arrangements are between you and the other student">
                    <p>
                        Any rental or swap agreement is a private matter between the students involved. ErasmusSwap does not
                        collect rent, hold deposits, verify identity, verify room condition, or guarantee outcomes. Meet in a
                        public place or via video call before committing money.
                    </p>
                </Section>

                <Section title="7. Intellectual property">
                    <p>
                        You keep ownership of the content you upload. By uploading, you grant ErasmusSwap a limited, worldwide,
                        royalty-free licence to host, display and share the content on the platform for the purpose of running
                        the service. The ErasmusSwap name, logo and interface are ours.
                    </p>
                </Section>

                <Section title="8. Termination">
                    <p>
                        You can delete your account at any time via <em>Profile → Danger zone</em>. We may terminate or suspend
                        your account, without prior notice, if you violate these terms or applicable law.
                    </p>
                </Section>

                <Section title="9. Disclaimer">
                    <p>
                        ErasmusSwap is provided "as is". We make no warranties about accuracy of listings, availability of the
                        service, or outcomes of any swap. To the maximum extent permitted by law we are not liable for indirect,
                        incidental, or consequential damages arising from use of the platform.
                    </p>
                </Section>

                <Section title="10. Changes to these terms">
                    <p>
                        We may update these terms as the service evolves. When we make a material change, we will bump the
                        version number and ask you to accept the new version the next time you open the app.
                    </p>
                </Section>

                <Section title="11. Contact">
                    <p>
                        Questions about these terms? Please use our{" "}
                        <Link to="/contact" className="text-primary hover:underline font-medium">contact form</Link> — we won't
                        publish any personal email address here.
                    </p>
                </Section>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="space-y-3">
            <h2 className="font-display text-xl sm:text-2xl font-semibold">{title}</h2>
            <div className="space-y-3">{children}</div>
        </section>
    );
}
